import {
  Component, ElementRef, OnDestroy, ViewChild,
  HostListener, EventEmitter, Output, OnInit,
  ChangeDetectorRef
} from '@angular/core';
import { CameraStateService } from '../../services/camera-state.service'
import { wrapAllMethodsWithLogging } from 'src/app/core/decorators/log-all-errors.decorator';
import { LoggingService } from 'src/app/services/logging.service';
export interface CapturedPhoto {
  id: string;
  buffer: ArrayBuffer;
  mimeType: string;
}
@Component({
  selector: 'app-custom-camera',
  templateUrl: './custom-camera.component.html',
  styleUrls: ['./custom-camera.component.scss'],
  standalone: false
})


export class CustomCameraComponent implements OnDestroy, OnInit {
  @ViewChild('videoElement') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasRef!: ElementRef<HTMLCanvasElement>;
  @Output() photoCaptured = new EventEmitter<CapturedPhoto>();
  @Output() stopCustomCamera = new EventEmitter<string>();
  @Output() startNativeCamera = new EventEmitter<boolean>();
  @Output() processingDone = new EventEmitter<void>()

  isProcessingPhoto = false;
  // ── Camera State ───────────────────────────────────────────────
  isCameraActive = false;
  isLoading = false;
  isProcessing = false;
  capturedImage: string | null = null;
  lastPhoto: string | null = null;
  errorMessage: string | null = null;
  facingMode: 'user' | 'environment' = 'environment';
  private stream: MediaStream | null = null;

  flashOn = false;
  showGrid = false;
  shutterPressed = false;
  currentZoom = 1;
  showFocusRing = false;
  focusX = 0;
  focusY = 0;
  private focusTimer: any;

  deviceOrientation = 'Portrait';
  capturedOrientation = 'Portrait';
  beta = 0;
  gamma = 0;
  alpha = 0;

  focusScore: number = 0;
  focusLabel: string = '';
  focusColor: string = '';
  isFocusChecking: boolean = false;

  private backCameraDeviceId: string | null = null;
  private frontCameraDeviceId: string | null = null;
  private devicesEnumerated = false;

  private readonly MAX_OPEN_RETRIES = 1;
  private readonly MAX_METADATA_RETRIES = 1;
  private readonly MAX_BLANK_RETRIES = 1;
  private readonly METADATA_TIMEOUT_MS = 5000;

  permissionDenied = false;
  browser: 'chrome' | 'safari' | 'firefox' | 'edge' | 'other' = 'other';
  zoomSliderOpen: boolean = false;
  isNativeCamera: boolean = false;
  orientationPermissionGranted = false;

  capturedPhoto: CapturedPhoto | null = null;
  previewUrl: string | null = null;
  componentName: string = 'custom-camera';
  constructor(private camState: CameraStateService, private cdr: ChangeDetectorRef,
    public loggingService: LoggingService,
  ) {
    wrapAllMethodsWithLogging(this, this.componentName);
  }

  get cameraState() { return this.camState.state; };

  ngOnInit(): void {
    this.camState.init();
    this.onStartCameraTap();
  }


  private detectBrowser(): 'chrome' | 'safari' | 'firefox' | 'edge' | 'other' {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('edg/')) return 'edge';
    if (ua.includes('chrome')) return 'chrome';
    if (ua.includes('safari')) return 'safari';
    if (ua.includes('firefox')) return 'firefox';
    return 'other';
  }



  // Bound listener reference so we can remove it later
  private readonly handleOrientation = (event: DeviceOrientationEvent) => {
    this.beta = event.beta ?? 0;
    this.gamma = event.gamma ?? 0;
    this.alpha = event.alpha ?? 0;
    this.deviceOrientation = this.calculateOrientation(this.beta, this.gamma);
  };

  // ── Orientation Icon
  get orientationIcon(): string {
    const icons: Record<string, string> = {
      'Portrait': 'phone-portrait-outline',
      'Portrait Upside Down': 'phone-portrait-outline',
      'Landscape Left': 'phone-landscape-outline',
      'Landscape Right': 'phone-landscape-outline',
      'Flat': 'tablet-landscape-outline',
    };
    return icons[this.deviceOrientation] ?? 'phone-portrait-outline';
  }

  private calculateOrientation(beta: number, gamma: number): string {
    if (beta === null || gamma === null || isNaN(beta) || isNaN(gamma)) {
      return 'Unknown';
    }

    const absBeta = Math.abs(beta);
    const absGamma = Math.abs(gamma);

    if (absBeta < 15 && absGamma < 15) return 'Flat';

    // Check landscape FIRST
    if (absGamma > absBeta && absGamma >= 30) {
      if (this.facingMode === 'user') {
        return gamma > 0 ? 'Landscape Left' : 'Landscape Right';
      } else {
        return gamma > 0 ? 'Landscape Right' : 'Landscape Left';
      }
    }

    // Portrait check second
    if (absBeta >= 30) {
      return beta > 0 ? 'Portrait' : 'Portrait Upside Down';
    }

    return 'Portrait';
  }

  private orientationToDegrees(
    orientation: string,
    facingMode: 'user' | 'environment'
  ): number {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    if (facingMode === 'environment') {
      if (isIOS) {
        const map: Record<string, number> = {
          'Portrait': 0,
          'Portrait Upside Down': 180,
          'Landscape Left': 270,
          'Landscape Right': 90,
          'Flat': 0,
        };
        return map[orientation] ?? 0;
      } else {
        const map: Record<string, number> = {
          'Portrait': 0,
          'Portrait Upside Down': 180,
          'Landscape Left': 270,
          'Landscape Right': 90,
          'Flat': 0,
        };
        return map[orientation] ?? 0;
      }
    } else {
      const map: Record<string, number> = {
        'Portrait': 0,
        'Portrait Upside Down': 180,
        'Landscape Left': 90,
        'Landscape Right': 270,
        'Flat': 0,
      };
      return map[orientation] ?? 0;
    }
  }

  async rotateImg(degrees: number): Promise<void> {
    if (!this.capturedPhoto?.buffer) return;
    this.isProcessing = true;
    try {
      const currentBlob = new Blob(
        [this.capturedPhoto.buffer],
        { type: this.capturedPhoto.mimeType }
      );
      const rotatedBlob = await this.rotateBlob(currentBlob, degrees);
      const newBuffer = await rotatedBlob.arrayBuffer();
      // Free old buffer before replacing
      this.capturedPhoto.buffer = null as any;
      // Update capturedPhoto with new rotated buffer
      this.capturedPhoto = {
        ...this.capturedPhoto,
        buffer: newBuffer
      };

      // Revoke old preview URL and create new one
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = URL.createObjectURL(
        new Blob([newBuffer], { type: this.capturedPhoto.mimeType })
      );
      this.cdr.detectChanges();

    } catch (error) {
      console.error('Error rotating image:', error);
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  private async enumerateAndSelectCamera(): Promise<void> {
    if (this.devicesEnumerated) return;
    try {
      const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
      tempStream.getTracks().forEach(t => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      const backCameras: MediaDeviceInfo[] = [];
      const frontCameras: MediaDeviceInfo[] = [];
      for (const cam of cameras) {
        const label = cam.label.toLowerCase();
        if (label.includes('front') || label.includes('user') || label.includes('selfie')) {
          frontCameras.push(cam);
        } else {
          backCameras.push(cam);
        }
      }
      this.backCameraDeviceId = await this.pickHighestResCamera(backCameras);
      this.frontCameraDeviceId = frontCameras[0]?.deviceId ?? null;
      this.devicesEnumerated = true;
    } catch (err) {
      this.camState.log('opening', 'ENUMERATE_FAILED', 'Camera enumeration failed', err);
    }
  }

  private async pickHighestResCamera(cameras: MediaDeviceInfo[]): Promise<string | null> {
    if (cameras.length === 0) return null;
    if (cameras.length === 1) return cameras[0].deviceId;
    let bestId = cameras[0].deviceId;
    let bestRes = 0;
    for (const cam of cameras) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: cam.deviceId }, width: { ideal: 9999 }, height: { ideal: 9999 } }
        });
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings();
        const res = (settings.width ?? 0) * (settings.height ?? 0);
        stream.getTracks().forEach(t => t.stop());
        if (res > bestRes) { bestRes = res; bestId = cam.deviceId; }
      } catch {
        this.camState.log('opening', 'PROBE_FAILED', `Could not probe camera: ${cam.label}`);
      }
    }
    return bestId;
  }
  // SUPPORT CHECK
  private checkSupport(): boolean {
    this.camState.markCheckingSupport();
    const supported = !!(navigator.mediaDevices?.getUserMedia);
    if (supported) {
      this.camState.markSupported();
    } else {
      this.camState.markNotSupported();
      this.errorMessage = 'Camera not supported in this browser.';
      console.log("Camera not supported in this browser")
      this.openNativeCamera();
    }
    return supported;
  }

  public openNativeCamera(): void {
    this.isNativeCamera = true;
    this.startNativeCamera.emit(true);
    this.camState.log('fallback_native', null, 'Opening native camera via input[capture]');

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    (input as any).capture = 'environment';

    input.onchange = async (e: any) => {
      this.startNativeCamera.emit(false);
      const file = e.target?.files?.[0];

      if (!file) return;

      this.isProcessing = true;
      this.camState.markSaving();

      try {
        // File → ArrayBuffer directly
        const buffer = await file.arrayBuffer();

        const snapshotOrientation = this.calculateOrientation(this.beta, this.gamma);
        const degrees = this.orientationToDegrees(snapshotOrientation, this.facingMode);
        this.capturedOrientation = snapshotOrientation;

        const finalBuffer = degrees !== 0
          ? await (await this.rotateBlob(
            new Blob([buffer], { type: file.type }), degrees
          )).arrayBuffer()
          : buffer;

        // Build CapturedPhoto
        const capturedPhoto: CapturedPhoto = {
          id: crypto.randomUUID(),
          buffer: finalBuffer,
          mimeType: file.type || 'image/jpeg'
        };

        // Clear input to free File reference
        input.value = '';
        this.camState.markSaveSuccess();
        this.isProcessingPhoto = true;
        this.photoCaptured.emit(capturedPhoto);
      } catch (err: any) {
        this.camState.markSaveFailed(err);
        this.camState.log('fallback_native', 'CAPTURE_FAILED',
          'Native camera capture failed', err);
        this.isProcessingPhoto = false;

      } finally {
        this.isProcessing = false;
        this.cdr.detectChanges();
      }
    };

    input.click();
  }

  // OPEN CAMERA  (with 1 retry)

  async onStartCameraTap(): Promise<void> {
    await this.requestOrientationPermission();
    await this.startCamera();
  }
  async startCamera(retryAttempt = 0): Promise<void> {
    this.errorMessage = null;
    this.permissionDenied = null;
    this.isLoading = true;

    // Support check
    if (!this.checkSupport()) {
      this.isLoading = false;
      return;
    }

    // Open camera 
    this.camState.markOpening();
    if (retryAttempt > 0) this.camState.markOpenRetry(retryAttempt);

    try {
      await this.enumerateAndSelectCamera();
      const deviceId = this.facingMode === 'environment'
        ? this.backCameraDeviceId
        : this.frontCameraDeviceId;

      const videoConstraints: MediaTrackConstraints = deviceId
        ? { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } }
        : { facingMode: { exact: this.facingMode }, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } };

      this.stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
      this.camState.markStreamOpened();

      const video = this.videoRef.nativeElement;
      video.srcObject = this.stream;

      // Metadata load 
      await this.waitForMetadata(video);
      await video.play();
      await this.applyStandardLensSettings();
      const DOE = DeviceOrientationEvent as any;
      const needsPermission = typeof DOE.requestPermission === 'function';
      if (!needsPermission || this.orientationPermissionGranted) {
        window.addEventListener('deviceorientation', this.handleOrientation, true);
      }
      this.isCameraActive = true;
      this.logCameraCapabilities();

    } catch (err: any) {
      console.log("err", err)
      if (err.name === 'NotAllowedError') {
        this.camState.markPermissionDenied(err);
        this.permissionDenied = true;
        this.browser = this.detectBrowser();
        const video = this.videoRef?.nativeElement;
        if (video) {
          video.srcObject = null;
          video.load();
        }
      } else if (
        err.name === 'NotFoundError' ||
        err.name === 'NotReadableError' || err.name === 'OverconstrainedError'
      ) {
        this.camState.markHardwareError(err);
        this.errorMessage = this.mapErrorMessage(err);
        if (retryAttempt < this.MAX_OPEN_RETRIES) {
          this.camState.log('opening', null, `Hardware error — retrying open (attempt ${retryAttempt + 1})`);
          this.stopStream();
          await this.startCamera(retryAttempt + 1);
          return;
        } else {
          this.camState.log('opening', 'OPEN_FATAL',
            'Camera open failed after retry — falling back to native camera');
          console.log("Camera not supported in this browser 111")
          this.openNativeCamera();
        }
      } else {
        this.camState.markHardwareError(err);
        this.errorMessage = `Camera error: ${err.message}`;
      }

    } finally {
      this.isLoading = false;
    }
  }

  // METADATA LOAD
  private waitForMetadata(video: HTMLVideoElement, retryAttempt = 0): Promise<void> {
    this.camState.markLoadingMetadata();
    return new Promise((resolve, reject) => {
      if (video.readyState >= 1) {
        this.camState.markMetadataLoaded(video.videoWidth, video.videoHeight);
        resolve();
        return;
      }
      const timeoutId = setTimeout(() => {
        video.removeEventListener('loadedmetadata', onLoaded);
        this.camState.markMetadataTimeout();

        if (retryAttempt < this.MAX_METADATA_RETRIES) {
          this.camState.log('loading_metadata', null, `Metadata timeout — retrying (attempt ${retryAttempt + 1})`);
          this.waitForMetadata(video, retryAttempt + 1).then(resolve).catch(reject);
        } else {
          reject(new Error('Video metadata failed to load after retry'));
        }
      }, this.METADATA_TIMEOUT_MS);

      const onLoaded = () => {
        clearTimeout(timeoutId);
        this.camState.markMetadataLoaded(video.videoWidth, video.videoHeight);
        resolve();
      };
      video.addEventListener('loadedmetadata', onLoaded, { once: true });
    });
  }
  //  CAMERA CONTROLS

  private async applyStandardLensSettings(): Promise<void> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    try {
      const capabilities = (track as any).getCapabilities?.();
      const advanced: any = {};
      if (capabilities?.focusMode?.includes('continuous')) advanced.focusMode = 'continuous';
      if (capabilities?.exposureMode?.includes('continuous')) advanced.exposureMode = 'continuous';
      if (capabilities?.whiteBalanceMode?.includes('continuous')) advanced.whiteBalanceMode = 'continuous';
      if (Object.keys(advanced).length > 0) {
        await (track as any).applyConstraints({ advanced: [advanced] });
      }
    } catch (err) {
      this.camState.log('ready', 'LENS_SETTINGS_FAILED', 'applyStandardLensSettings failed', err);
    }
  }

  private async setFocusMode(mode: 'continuous' | 'single-shot'): Promise<void> {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    try {
      const capabilities = (track as any).getCapabilities?.();
      if (!capabilities?.focusMode?.includes(mode)) return;
      await (track as any).applyConstraints({ advanced: [{ focusMode: mode }] });
    } catch (err) {
      this.camState.log('ready', 'FOCUS_MODE_FAILED', `Failed to set focus mode: ${mode}`, err);
    }
  }

  async switchCamera(): Promise<void> {
    this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    this.stopStream();
    await this.startCamera();
  }

  async setZoom(level: number): Promise<void> {
    this.currentZoom = level;
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;

    try {
      const capabilities = (track as any).getCapabilities?.();
      if (capabilities?.zoom) {
        const { min = 1, max = 1 } = capabilities.zoom;
        const clamped = Math.max(min, Math.min(level, max));
        await (track as any).applyConstraints({ zoom: clamped });
      } else {
        const video = this.videoRef?.nativeElement;
        if (video) {
          video.style.transform = `scale(${level})`;
          video.style.transformOrigin = 'center center';
        }
      }
    } catch (err) {
      this.camState.log('ready', 'ZOOM_FAILED', 'Zoom not supported', err);
    }
  }

  snapZoom(level: number): void {
    this.zoomSliderOpen = false;
    this.setZoom(level);
  }

  onZoomSlider(event: Event): void {
    const raw = parseInt((event.target as HTMLInputElement).value);
    const level = raw / 10; // 10→1.0, 15→1.5, 20→2.0
    this.setZoom(level);
  }

  async toggleFlash(): Promise<void> {
    this.flashOn = !this.flashOn;
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    try {
      await (track as any).applyConstraints({ advanced: [{ torch: this.flashOn }] });
    } catch {
      this.flashOn = false;
      this.camState.log('ready', 'TORCH_NOT_SUPPORTED', 'Torch not supported on this device');
      alert('Torch not supported on this device');
    }
  }

  // CAPTURE
  async capturePhoto(): Promise<void> {
    this.camState.markCapturing();
    this.shutterPressed = true;
    setTimeout(() => this.shutterPressed = false, 150);

    const video = this.videoRef.nativeElement;
    const canvas = this.canvasRef.nativeElement;
    try {
      const track = this.stream?.getVideoTracks()[0];
      const settings = track?.getSettings();
      const srcW = settings?.width ?? video.videoWidth;
      const srcH = settings?.height ?? video.videoHeight;

      canvas.width = srcW;
      canvas.height = srcH;
      const ctx = canvas.getContext('2d', { alpha: false })!;
      ctx.imageSmoothingEnabled = false;

      if (this.facingMode === 'user') {
        ctx.translate(srcW, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, 0, 0, srcW, srcH);
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/jpeg', 0.92)
      );
      // const rawBase64 = canvas.toDataURL('image/jpeg', 0.98);

      canvas.width = 1;
      canvas.height = 1;
      ctx.clearRect(0, 0, 1, 1);

      const snapshotOrientation = this.calculateOrientation(this.beta, this.gamma);
      const degrees = this.orientationToDegrees(snapshotOrientation, this.facingMode);
      this.capturedOrientation = snapshotOrientation;

      this.stopStream();
      window.removeEventListener('deviceorientation', this.handleOrientation, true);

      this.isProcessing = true;
      const finalBlob = degrees !== 0
        ? await this.rotateBlob(blob, degrees)
        : blob;

      let buffer: ArrayBuffer | null = await finalBlob.arrayBuffer(); //convert ONCE, discard blob

      // Store CapturedPhoto object — no base64 string anywhere
      this.capturedPhoto = { id: crypto.randomUUID(), buffer, mimeType: 'image/jpeg' };
      // For preview display only — create Object URL, revoke on destroy
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = URL.createObjectURL(new Blob([buffer], { type: 'image/jpeg' }));
      buffer = null;

      // this.capturedImage = finalImage;
      // this.lastPhoto = finalImage;
      this.camState.markCaptureSuccess();
      // await this.checkFocusQuality(this.capturedPhoto);

    } catch (err: any) {
      this.camState.markCaptureFailed(err);
      // ── Failure after first capture → restart once ─────────────
      const wasFirstCapture = this.camState.state.totalCapturesPerOpenCameraSession === 0;
      if (wasFirstCapture) {
        this.camState.log('capturing', null, 'First capture failed — restarting camera once');
        this.stopStream();
        try {
          await this.startCamera();
        } catch (restartErr) {
          this.camState.log('capturing', 'RESTART_FAILED',
            'Camera restart after capture failure also failed — opening native camera', restartErr);
          console.log("Camera not supported in this browser 222")
          this.openNativeCamera();
        }
      } else {
        this.camState.log('capturing', null, 'Capture failed mid-session — opening native camera');
        console.log("Camera not supported in this browser 333")
        this.openNativeCamera();
      }
    } finally {
      this.isProcessing = false;
    }
  }

  private rotateBlob(blob: Blob, degrees: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url); // free immediately after load
        const normalized = ((degrees % 360) + 360) % 360;
        const swap = normalized === 90 || normalized === 270;
        const offscreen = document.createElement('canvas');
        offscreen.width = swap ? img.height : img.width;
        offscreen.height = swap ? img.width : img.height;
        const ctx = offscreen.getContext('2d')!;
        ctx.translate(offscreen.width / 2, offscreen.height / 2);
        ctx.rotate((normalized * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        offscreen.toBlob(b => {
          // Shrink offscreen canvas immediately after toBlob
          offscreen.width = 1;
          offscreen.height = 1;

          b ? resolve(b) : reject(new Error('rotate toBlob failed'));
        }, 'image/jpeg', 0.92);
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  //  SAVE (emit to parent)
  usephoto(): void {
    if (!this.capturedPhoto || this.isProcessingPhoto) return;
    this.camState.markSaving();
    try {
      this.isProcessingPhoto = true;
      this.photoCaptured.emit(this.capturedPhoto);
      this.camState.markSaveSuccess();
    } catch (err: any) {
      this.camState.markSaveFailed(err);
      this.camState.log('saving', null, 'Retrying emit after save failure');
      try {
        this.photoCaptured.emit(this.capturedPhoto);
        this.camState.markSaveSuccess();
      } catch (retryErr: any) {
        this.camState.markSaveFailed(retryErr);
      }
    }
  }
  resetProcessing(): void {
    this.isProcessingPhoto = false;
  }
  //  PREVIEW ACTIONS
  viewLastPhoto(): void {
    if (this.lastPhoto) {
      this.capturedImage = this.lastPhoto;
      this.stopStream();
    }
  }

  downloadPhoto() {
    this.camState.log('saving', null, 'downloadPhoto triggered');
  }

  async retake(): Promise<void> {
    this.currentZoom = 1;
    this.flashOn = false;
    // Clear new variables
    if (this.capturedPhoto) {
      this.capturedPhoto.buffer = null as any;
      this.capturedPhoto = null;
    }
    // Revoke preview URL to free memory
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
    // Reset processing flag in case retake pressed during processing
    this.isProcessingPhoto = false;
    await this.startCamera();
  }

  stopCamera(): void {
    this.stopStream();
    this.camState.markSessionEnded('user_stop');
    this.stopCustomCamera.emit('true');
    window.removeEventListener('deviceorientation', this.handleOrientation, true);
  }

  @HostListener('click', ['$event'])
  async onTap(event: MouseEvent): Promise<void> {
    if (!this.isCameraActive || this.capturedImage) return;
    const target = event.target as HTMLElement;
    if (
      target.closest('button') || target.closest('.bottom-bar') ||
      target.closest('.top-bar') || target.closest('.zoom-bar') ||
      target.closest('.mode-bar')
    ) return;

    this.focusX = event.clientX - 30;
    this.focusY = event.clientY - 30;
    this.showFocusRing = true;
    clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => this.showFocusRing = false, 900);

    const track = this.stream?.getVideoTracks()[0];
    const capabilities = (track as any)?.getCapabilities?.();
    if (capabilities?.focusMode?.includes('single-shot')) {
      await this.setFocusMode('single-shot');
      await new Promise(r => setTimeout(r, 300));
      setTimeout(async () => {
        await this.setFocusMode('continuous');
      }, 1500);
    } else {
      await this.setFocusMode('continuous');
    }
  }

  //  HELPERS
  private stopStream(): void {
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = null;
    this.isCameraActive = false;
    this.camState.markSessionEnded('stream_stop');
  }

  private mapErrorMessage(err: any): string {
    const map: Record<string, string> = {
      NotAllowedError: 'Camera permission denied. Please allow access in settings.',
      NotFoundError: 'No camera found on this device.',
      NotReadableError: 'Camera is already in use by another app.',
      OverconstrainedError: 'Camera constraints could not be satisfied.',
    };
    return map[err.name] ?? `Camera error: ${err.message}`;
  }

  private handleCameraError(err: any): void {
    this.errorMessage = this.mapErrorMessage(err);
  }

  private logCameraCapabilities(): void {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    const capabilities = (track as any).getCapabilities?.();
    const settings = track.getSettings();
    const mp = ((settings.width ?? 0) * (settings.height ?? 0) / 1_000_000).toFixed(1);
    this.camState.log('ready', null,
      `Camera ready — ${settings.width}×${settings.height} (${mp}MP) @ ${settings.frameRate}fps`);
  }

  ngOnDestroy(): void {
    this.stopStream();
    clearTimeout(this.focusTimer);
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl); // always clean up
    this.camState.markSessionEnded('component_destroy');
    window.removeEventListener('deviceorientation', this.handleOrientation, true);
  }

  //  FOCUS QUALITY CHECK (unchanged logic, added state log)
  private async checkFocusQuality(base64: string): Promise<void> {
    this.isFocusChecking = true;
    try {
      const score = await this.calculateLaplacianVariance(base64);
      this.focusScore = Math.round(score);
      if (score >= 500) { this.focusLabel = 'Excellent'; this.focusColor = '#00e676'; }
      else if (score >= 200) { this.focusLabel = 'Good'; this.focusColor = '#69f0ae'; }
      else if (score >= 80) { this.focusLabel = 'Fair'; this.focusColor = '#FFD700'; }
      else if (score >= 30) { this.focusLabel = 'Blurry'; this.focusColor = '#ff9100'; }
      else { this.focusLabel = 'Very Blurry'; this.focusColor = '#ff1744'; }

      this.camState.log('capturing', null,
        `Focus quality: ${this.focusLabel} (score: ${this.focusScore})`);
    } catch (err) {
      this.camState.log('capturing', 'FOCUS_CHECK_FAILED', 'Laplacian focus check failed', err);
      this.focusLabel = 'Unknown';
      this.focusColor = '#ffffff';
    } finally {
      this.isFocusChecking = false;
    }
  }

  private calculateLaplacianVariance(base64: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 640 / img.width, 480 / img.height);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const gray = new Float32Array(width * height);
        for (let i = 0; i < width * height; i++) {
          gray[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
        }
        const laplacian = new Float32Array(width * height);
        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            const idx = y * width + x;
            laplacian[idx] = gray[idx - width] + gray[idx + width] + gray[idx - 1] + gray[idx + 1] + gray[idx] * -4;
          }
        }
        let sum = 0, count = 0;
        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) { sum += laplacian[y * width + x]; count++; }
        }
        const mean = sum / count;
        let variance = 0;
        for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
            const diff = laplacian[y * width + x] - mean;
            variance += diff * diff;
          }
        }
        resolve(variance / count);
      };
      img.onerror = () => reject(new Error('Failed to load image for focus check'));
      img.src = base64;
    });
  }

  // Add New --------------------------
  private async requestOrientationPermission(): Promise<void> {
    const DOE = DeviceOrientationEvent as any;
    if (typeof DOE.requestPermission !== 'function') {
      this.camState.markOrientationNotSupported()
      this.orientationPermissionGranted = true;
      return;
    }

    try {
      const permission = await DOE.requestPermission();
      if (permission === 'granted') {
        this.orientationPermissionGranted = true;
        this.camState.markOrientationGranted()
      } else {
        this.orientationPermissionGranted = false;
        this.camState.markOrientationDenied()
      }

    } catch (err: any) {
      this.orientationPermissionGranted = false;
      this.camState.markOrientationError(err)
    }
  }
}
